/**
 * Google as a sign-in platform and a data connection (slice C of the identity design,
 * 20-identity-design.md §2.4, §3.3, §3.5, §4.3; client routes in
 * 40-client-apps-design.md §1.3, §4.3, §6).
 *
 * Pure: the authorize URL builder the portal uses (it keeps PKCE state in
 * sessionStorage, like the Lark web login), the scopes, the `hd` rule, and the request
 * and response shapes of the Google routes. The API worker redeems codes and validates
 * tokens; nothing here talks to Google.
 *
 * Vendor facts are from Google's documentation (OpenID Connect, OAuth 2.0 for web server
 * apps, incremental authorization, Admin SDK Directory API), read 2026-10-16. What still
 * needs a live check is listed in the design's §12 (C).
 */
import { normalizeGoogleDomain, type Platform } from './identity'
import {
  clientRedirectUri,
  type ClientAuthorizeResponse,
  type ClientCompleteRequest,
  type ClientConnectResult,
  type ClientEnvironment,
  type ClientKind,
  type ClientSessionResponse,
  type ClientStartRequest,
} from './clientApps'
import type { AuthLoginResponse } from './api'
import type { IDENTITY_ERROR_CODES } from './auth'

// =============================================================================
// Endpoints
// =============================================================================

export const GOOGLE_AUTHORIZE_URL = 'https://accounts.google.com/o/oauth2/v2/auth'
export const GOOGLE_TOKEN_URL = 'https://oauth2.googleapis.com/token'
export const GOOGLE_REVOKE_URL = 'https://oauth2.googleapis.com/revoke'
/** Google's signing keys for ID tokens (the Sheets add-on's identity token is one). */
export const GOOGLE_JWKS_URL = 'https://www.googleapis.com/oauth2/v3/certs'
/** Admin SDK: the signed-in person, as the directory sees them (super-admin takeover). */
export const GOOGLE_DIRECTORY_ME_URL = 'https://admin.googleapis.com/admin/directory/v1/users/me'

/** Google writes `iss` either way; both are Google (OpenID Connect, "Validating an ID token"). */
export const GOOGLE_ISSUERS: readonly string[] = ['https://accounts.google.com', 'accounts.google.com']

// =============================================================================
// Scopes
// =============================================================================

/**
 * Sign-in asks for identity only, so the first consent screen is the smallest one.
 * Non-sensitive: brand verification only.
 */
export const GOOGLE_SIGNIN_SCOPES = ['openid', 'email', 'profile'] as const

/**
 * The one data scope (owner decision §11.5): files the person opened through AutomaDoc
 * (the Picker) or AutomaDoc created. Non-sensitive and "Recommended" for the Sheets API —
 * `spreadsheets` would be sensitive (video review), `drive`/`drive.readonly` restricted
 * (a yearly third-party assessment).
 */
export const GOOGLE_DRIVE_FILE_SCOPE = 'https://www.googleapis.com/auth/drive.file'

/**
 * Asked once, for the super-admin takeover and for linking a Workspace to an account
 * (§3.3): it lets us read `isAdmin` of the person signing in. Never stored with a
 * connection, never used again (see `storableGoogleScopes`).
 */
export const GOOGLE_ADMIN_DIRECTORY_SCOPE = 'https://www.googleapis.com/auth/admin.directory.user.readonly'

/** Why a person connects Google data. Only Sheets today; a Drive output folder may follow. */
export const GOOGLE_CONNECT_PURPOSES = ['sheets'] as const
export type GoogleConnectPurpose = (typeof GOOGLE_CONNECT_PURPOSES)[number]

export function isGoogleConnectPurpose(value: unknown): value is GoogleConnectPurpose {
  return typeof value === 'string' && (GOOGLE_CONNECT_PURPOSES as readonly string[]).includes(value)
}

/** What a connection for `purpose` asks for: sign-in scopes plus the data scope. */
export function googleConnectScopes(purpose: GoogleConnectPurpose): string[] {
  switch (purpose) {
    case 'sheets':
      return [...GOOGLE_SIGNIN_SCOPES, GOOGLE_DRIVE_FILE_SCOPE]
  }
}

/** What the takeover and the link proof ask for: who you are, and whether you administer the Workspace. */
export const GOOGLE_ADMIN_PROOF_SCOPES = [...GOOGLE_SIGNIN_SCOPES, GOOGLE_ADMIN_DIRECTORY_SCOPE] as const

/**
 * Google answers `email` and `profile` with their long names in the token response's
 * `scope` ("…/auth/userinfo.email"). One spelling, so a granted scope is recognised
 * however it was written.
 */
const SCOPE_ALIASES: Record<string, string> = {
  email: 'https://www.googleapis.com/auth/userinfo.email',
  profile: 'https://www.googleapis.com/auth/userinfo.profile',
}

function canonicalScope(scope: string): string {
  return SCOPE_ALIASES[scope] ?? scope
}

/** The scopes in a space-separated `scope` string, canonical, deduplicated and sorted. */
export function parseGoogleScopes(scope: string | null | undefined): string[] {
  return [...new Set((scope ?? '').split(/\s+/).filter(Boolean).map(canonicalScope))].sort()
}

/** Whether `granted` (a token response's `scope`) includes `wanted`. */
export function hasGoogleScope(granted: string | null | undefined, wanted: string): boolean {
  return parseGoogleScopes(granted).includes(canonicalScope(wanted))
}

/**
 * The scopes worth recording on a connection: those AutomaDoc uses. The admin-directory
 * scope is left out on purpose (§3.3: used once, not kept) — with
 * `include_granted_scopes`, Google may hand it back on a later sign-in, and a stored
 * scope list that named it would invite someone to use it.
 */
export function storableGoogleScopes(granted: string | null | undefined): string {
  const keep = new Set(['openid', ...GOOGLE_SIGNIN_SCOPES.map(canonicalScope), GOOGLE_DRIVE_FILE_SCOPE])
  return parseGoogleScopes(granted)
    .filter((s) => keep.has(s))
    .join(' ')
}

// =============================================================================
// The organisation: the `hd` rule (§2.4, §3.5 rule 3)
// =============================================================================

/**
 * `oauth_connections.provider_tenant_id` of a Google person with no Workspace — their
 * account is an email-platform account, which has no organisation id.
 */
export const GOOGLE_PERSONAL_TENANT = 'personal'

export type GoogleOrg =
  /** A Workspace / Cloud Identity user: the account is `google:<hd>`. */
  | { kind: 'workspace'; hd: string }
  /** No `hd`: a consumer Google account. The account is a personal email-platform one. */
  | { kind: 'personal' }
  /** `hd` present but not a domain: refuse rather than guess. */
  | { kind: 'invalid' }

/**
 * Which organisation a Google sign-in belongs to — **only the `hd` claim decides**.
 *
 * `hd` is present only for Workspace and Cloud Identity accounts and is Google's word
 * that the account is managed by that domain. The *email's* domain proves nothing: a
 * consumer Google account can be registered with `alice@acme.com`, has no `hd`, and must
 * never join acme.com's account. Without `hd` the person gets a personal account of
 * their own (an email-platform account), never a domain account.
 */
export function googleOrgOf(claims: { hd?: unknown }): GoogleOrg {
  if (claims.hd === undefined || claims.hd === null || claims.hd === '') return { kind: 'personal' }
  if (typeof claims.hd !== 'string') return { kind: 'invalid' }
  const hd = normalizeGoogleDomain(claims.hd)
  return hd ? { kind: 'workspace', hd } : { kind: 'invalid' }
}

// =============================================================================
// The authorize URL
// =============================================================================

export interface GoogleAuthorizeParams {
  clientId: string
  /** Must match, exactly, what is later sent with the code (Google compares them). */
  redirectUri: string
  /** Opaque anti-CSRF value, echoed back on the redirect. */
  state: string
  /** Echoed in the ID token; the API compares it. */
  nonce: string
  /** PKCE S256 challenge of a verifier the caller keeps. */
  codeChallenge: string
  scopes: readonly string[]
  /**
   * 'offline' for anything that keeps a connection (a refresh token comes back only on
   * consent); 'online' for the one-shot admin proof, which must leave nothing behind.
   */
  accessType?: 'offline' | 'online'
  /** Incremental authorization: the new token also covers what was granted before. */
  includeGrantedScopes?: boolean
  /**
   * 'consent' only when the API holds no refresh token for the person — Google returns
   * one only on a consent screen, and asking every time would nag people who already
   * agreed. 'select_account' for the admin proof (pick the admin account).
   */
  prompt?: 'consent' | 'select_account' | 'select_account consent'
  /** An email or a Google `sub`: preselects the account. */
  loginHint?: string
}

/**
 * The URL that sends a browser to Google's sign-in and consent screen. Always the code
 * flow with PKCE (S256) and a nonce; the code is redeemed by the API as a confidential
 * client, never in the browser.
 */
export function buildGoogleAuthorizeUrl(params: GoogleAuthorizeParams): string {
  const url = new URL(GOOGLE_AUTHORIZE_URL)
  url.searchParams.set('client_id', params.clientId)
  url.searchParams.set('redirect_uri', params.redirectUri)
  url.searchParams.set('response_type', 'code')
  url.searchParams.set('scope', params.scopes.join(' '))
  url.searchParams.set('state', params.state)
  url.searchParams.set('nonce', params.nonce)
  url.searchParams.set('code_challenge', params.codeChallenge)
  url.searchParams.set('code_challenge_method', 'S256')
  url.searchParams.set('access_type', params.accessType ?? 'offline')
  if (params.includeGrantedScopes ?? true) url.searchParams.set('include_granted_scopes', 'true')
  if (params.prompt) url.searchParams.set('prompt', params.prompt)
  if (params.loginHint) url.searchParams.set('login_hint', params.loginHint)
  return url.toString()
}

/**
 * The portal's redirect URI for Google web sign-in in an environment: the portal's root,
 * `https://<portal host>/` (§4.1). Derived from the client redirect hosts so the two
 * lists cannot disagree on the portal's host (Teams uses the portal's `/auth/done.html`).
 */
export function googlePortalRedirectUri(environment: ClientEnvironment): string {
  return new URL(clientRedirectUri(environment, 'teams')).origin + '/'
}

/** A Google spreadsheet id as the Drive API writes it (letters, digits, '-' and '_'). */
export function isGoogleSpreadsheetId(value: unknown): value is string {
  return typeof value === 'string' && /^[A-Za-z0-9_-]{20,128}$/.test(value)
}

// =============================================================================
// Error codes of the Google routes
// =============================================================================

export const GOOGLE_ERROR_CODES = {
  /** 503: the environment has no Google client configured (id, secret, sealing secret). */
  GOOGLE_NOT_CONFIGURED: 'GOOGLE_NOT_CONFIGURED',
  /** 400: the redirect URI is not this environment's portal root. */
  INVALID_REDIRECT_URI: 'INVALID_REDIRECT_URI',
  /** 400: Google refused the code (expired, used, wrong verifier). Start again. */
  CODE_INVALID: 'CODE_INVALID',
  /** 401: an ID token from the code exchange failed a check (iss, aud, exp, nonce, sub). */
  INVALID_ID_TOKEN: 'INVALID_ID_TOKEN',
  /** 403: Google has not verified the address; nothing may rest on it. */
  EMAIL_NOT_VERIFIED: 'EMAIL_NOT_VERIFIED',
  /**
   * 409: no refresh token came back and none is held — run the same sign-in again with
   * `prompt=consent` (Google returns a refresh token only on a consent screen).
   */
  GOOGLE_CONSENT_REQUIRED: 'GOOGLE_CONSENT_REQUIRED',
  /** 403: the person unticked a scope AutomaDoc needs on Google's consent screen. */
  SCOPE_NOT_GRANTED: 'SCOPE_NOT_GRANTED',
  /** 403: the person declined on Google's screen (`error=access_denied`). */
  CONSENT_DENIED: 'CONSENT_DENIED',
  /** 409: the caller has no Google connection with Drive file access. */
  GOOGLE_CONNECTION_REQUIRED: 'GOOGLE_CONNECTION_REQUIRED',
  /** 409: the connection's grant is gone (revoked, expired): connect again. */
  GOOGLE_REAUTH_REQUIRED: 'GOOGLE_REAUTH_REQUIRED',
  /** 503: the Picker's API key or project number is not configured. */
  PICKER_UNAVAILABLE: 'PICKER_UNAVAILABLE',
  /** 403: the Directory API says this person is not a super admin of the Workspace. */
  NOT_WORKSPACE_ADMIN: 'NOT_WORKSPACE_ADMIN',
  /** 409: a takeover on an account that is not a Workspace's (a personal account). */
  NOT_A_WORKSPACE_ACCOUNT: 'NOT_A_WORKSPACE_ACCOUNT',
  /** 403: the Workspace the person administers is not the one this account answers for. */
  WORKSPACE_MISMATCH: 'WORKSPACE_MISMATCH',
} as const

export type GoogleErrorCode =
  | (typeof GOOGLE_ERROR_CODES)[keyof typeof GOOGLE_ERROR_CODES]
  // The client-route codes Google shares with Microsoft live once, in auth.ts
  // (IDENTITY_ERROR_CODES): a token that failed verification, a used or unknown attempt,
  // a grant handed back by someone else.
  | typeof IDENTITY_ERROR_CODES.INVALID_CLIENT_TOKEN
  | typeof IDENTITY_ERROR_CODES.ATTEMPT_INVALID
  | typeof IDENTITY_ERROR_CODES.CONNECTION_USER_MISMATCH
  | typeof IDENTITY_ERROR_CODES.CONNECT_IN_PORTAL

// =============================================================================
// API shapes
// =============================================================================

/** The code and what proves it is ours: PKCE verifier and nonce from the portal's sessionStorage. */
export interface GoogleCodeRequest {
  code: string
  /** The portal root it was issued for (`googlePortalRedirectUri`). */
  redirectUri: string
  codeVerifier: string
  nonce: string
}

/** POST /api/auth/google/login */
export interface GoogleLoginRequest extends GoogleCodeRequest {
  locale?: string
  /** An invitation into a Workspace account, accepted if this person is of that Workspace. */
  invitation?: import('./emailAuth').InvitationProof
}

/** The Lark login's shape (the portal's auth store reads it), plus what is new. */
export interface GoogleLoginResponse extends AuthLoginResponse {
  /**
   * The session's platform, always the account's: 'google' for a Workspace account,
   * 'email' for a personal Google account (it signs in to the email account of its
   * verified address, as the emailed code would).
   */
  plat: Extract<Platform, 'google' | 'email'>
  /** The account's platform: 'google' for a Workspace, 'email' for a personal Google account. */
  accountPlatform: Platform
  email: string
  isAdmin: boolean
  isNewAccount: boolean
}

/** POST /api/auth/google/addon */
export interface GoogleAddonLoginRequest {
  identityToken: string
  /** Telemetry only (40 §4.3); never authorises anything. */
  spreadsheetId?: string
  locale?: string
}

/** POST /api/auth/google/client-start — the shape both providers take (clientApps.ts). */
export type GoogleClientStartRequest = ClientStartRequest

export type GoogleAuthorizeUrlResponse = ClientAuthorizeResponse

/** POST /api/auth/google/client-complete — what `/auth/done.html` read from the redirect. */
export type GoogleClientCompleteRequest = ClientCompleteRequest

/** What a client's Picker step needs (40 §4.3 step 4). */
export interface GooglePickerGrant {
  /** The caller's own access token, minted for this Picker; about an hour. */
  accessToken: string
  expiresAt: number
  /** The Cloud project number (`setAppId`): the grant is made to our project. */
  appId: string
  /** The browser API key restricted to the Picker API and our hosts. */
  developerKey: string
}

export interface GoogleConnectCompleteResponse extends ClientConnectResult {
  purpose: GoogleConnectPurpose
  /** Only for purpose 'sheets': the Picker that grants the spreadsheet. */
  picker?: GooglePickerGrant & { fileId?: string }
}

export type GoogleClientCompleteResponse = ClientSessionResponse | GoogleConnectCompleteResponse

/** POST /api/tenant/connections/google */
export interface GoogleConnectRequest {
  purpose: GoogleConnectPurpose
  /** A client app: the API holds the attempt and returns the URL. Absent: the portal builds it. */
  client?: ClientKind
  context?: { spreadsheetId?: string }
  /**
   * Sheets: a fresh Apps Script identity token (`ScriptApp.getIdentityToken()`). The
   * popup's grant must come back as that Google account; a person who signed in with
   * Google or connected before needs none.
   */
  identityToken?: string
}

/**
 * The portal's answer (no `client`): what to put in `buildGoogleAuthorizeUrl`. The portal
 * keeps PKCE and nonce in sessionStorage and posts the code to `…/complete`.
 */
export interface GoogleConnectPlan {
  scopes: string[]
  prompt: 'consent' | null
  /** The Google `sub` to preselect (a Workspace person's own, or the account connected before); empty when there is none. */
  loginHint: string
  redirectUri: string
  clientId: string
}

export type GoogleConnectResponse = GoogleAuthorizeUrlResponse | GoogleConnectPlan

/** GET /api/tenant/connections/google — the caller's own connection, never a token. */
export interface GoogleConnectionStatus {
  connected: boolean
  status: 'active' | 'reauth_required' | 'revoked' | null
  scopes: string[]
  /** Whether the Picker and the Sheets source can work (`drive.file` granted). */
  driveFile: boolean
  /** The Google account the grant was made as (Workspace domain or 'personal'), for "Connected as …". */
  account?: { tenantId: string; personal: boolean; address: string | null } | null
}

/** GET /api/tenant/connections/google/picker-token */
export type GooglePickerTokenResponse = GooglePickerGrant

/** POST /api/tenant/connections/google/takeover */
export interface GoogleTakeoverRequest extends GoogleCodeRequest {
  /** Also take admin from whoever became admin by arriving first (`first_user`). */
  demoteFirstUser?: boolean
}

export interface GoogleTakeoverResponse {
  success: true
  role: 'admin'
  /** Admins who were demoted (when asked). */
  demoted: string[]
  /** Admins who were told (or would be, once a notifier is plugged in). */
  notified: string[]
}

